import React, { useMemo, useState } from "react"
import clsx from "clsx"

import {
	Button,
	Grid,
	IconButton,
	Input,
	InputAdornment,
	ListItem,
	ListItemIcon,
	ListItemText,
	Tab,
	Tabs,
	Typography
} from "@material-ui/core"
import {
	Close as CloseIcon,
	FiberManualRecord as CircleBulletIcon,
	HelpOutline as DefaultIcon,
	Search as SearchIcon
} from "@material-ui/icons"

import { createFilterOptions } from "@material-ui/lab/useAutocomplete"

import { Divider, AIChip } from "@/components"

import useStyles from "@/pages/Admin/Flow/FlowConstructor/FlowEditor/components/BlockCreator/styles"
import useChatBotFlowConstructorStore
	from "@/pages/Admin/Flow/FlowConstructor/FlowEditor/hooks/useChatBotFlowConstructorStore"

import { blockThemeConfig } from "@/pages/Admin/Flow/FlowConstructor/FlowEditor/config/blockTheme"
import { blockCategoryThemeConfig } from "@/pages/Admin/Flow/FlowConstructor/FlowEditor/config/blockCategoryTheme"

import { ChatBotFlowBlockType } from "@/protocols/chatBot"

import AsideDrawer from "@/pages/Admin/Flow/FlowConstructor/FlowEditor/components/AsideDrawer"
import { isBlockedBlock } from "@/pages/Admin/Flow/FlowConstructor/FlowEditor/utils/chatBotFlow"

import BlockAlertDialog from "@/pages/Admin/Flow/FlowConstructor/FlowEditor/components/BlockAlertDialog"
import AIBlockTooltip from "@/pages/Admin/Flow/FlowConstructor/FlowEditor/components/AIBlockTooltip"
import UpsellHandlerDialog from "@/components/UpsellDialog/UpsellHandlerDialog"
import useSubscriptionLimits from "@/hooks/useSubscriptionLimits"
import { colors } from "@/styles/colors"

export const DRAG_AND_DROP_BLOCK_TYPE_KEY = "letalk-chat-bot/block-type"

type DisabledBlockConfig = {
	disabled: boolean
}

type FlatBlock = {
	block: {
		id: number
		title: string
		description: string
		type: ChatBotFlowBlockType
		has_ai_capabilities?: boolean
		keywords?: string[]
	}
	categoryTitle: string
	categoryType: string
}

const filterBlockOptions = createFilterOptions<FlatBlock>({
	ignoreAccents: true,
	ignoreCase: true,
	matchFrom: "any",
	trim: true,
	stringify: (option) => [
		option.block.title,
		option.block.description,
		option.block.type,
		option.categoryTitle,
		option.categoryType,
		...(option.block.keywords || [])
	]
		.filter(Boolean)
		.join(" ")
})

const aiBlockToShowUpsellDialog: Partial<Record<ChatBotFlowBlockType, boolean>> = {
	"ai-intention": true,
	"ai-condition": true,
	"ai-manage-client-tags": true,
	"custom-field-fill": true
}

type BlockCreatorTabsOptions = "general" | "ai"

const BlockCreator: React.FC = () => {
	const classes = useStyles()
	const chatBotFlowConstructorStore = useChatBotFlowConstructorStore()

	const aiCreditsPermissionLimit = useSubscriptionLimits("permission", "ai_resources")

	const [opened, setOpened] = useState(true)
	const [searchTerm, setSearchTerm] = useState("")
	const [selectedTab, setSelectedTab] = useState<BlockCreatorTabsOptions>("general")
	const normalizedSearchTerm = searchTerm.trim()
	const blockCategories = chatBotFlowConstructorStore.constructionResources.blockCategories

	const handleTabChange = (tab: BlockCreatorTabsOptions) => {
		setSelectedTab(tab)
	}

	const flatBlocks: FlatBlock[] = useMemo(() => {
		return blockCategories.flatMap(category =>
			category.blocks.map(block => ({
				block,
				categoryTitle: category.title,
				categoryType: category.type
			}))
		)
	}, [blockCategories])

	const matchedFlatBlocks = useMemo(() => {
		if (!normalizedSearchTerm) {
			return flatBlocks
		}

		return filterBlockOptions(flatBlocks, {
			inputValue: normalizedSearchTerm,
			getOptionLabel: option => option.block.title
		})
	}, [flatBlocks, normalizedSearchTerm])

	const matchedBlockIds = useMemo(() => {
		return new Set(matchedFlatBlocks.map((flatBlock) => flatBlock.block.id))
	}, [matchedFlatBlocks])

	const filteredCategories = useMemo(() => {
		return blockCategories
			.map((category) => ({
				...category,
				blocks: category.blocks.filter((block) => {
					const matchesSearch = !normalizedSearchTerm || matchedBlockIds.has(block.id)
					const matchesTab = selectedTab === "ai" ? Boolean(block.has_ai_capabilities) : true

					return matchesSearch && matchesTab
				})
			}))
			.filter((category) => category.blocks.length > 0)
	}, [blockCategories, selectedTab, matchedBlockIds, normalizedSearchTerm])

	const handleDragStart = (event: React.DragEvent<HTMLDivElement>, blockType: ChatBotFlowBlockType) => {
		const shouldShowUpsellDialog = aiCreditsPermissionLimit.mustBlock && aiBlockToShowUpsellDialog[blockType]
		if (shouldShowUpsellDialog) {
			UpsellHandlerDialog.open({
				type: "ai-credits-limit-reached"
			})
			return
		}

		event.dataTransfer.setData(DRAG_AND_DROP_BLOCK_TYPE_KEY, blockType)
		event.dataTransfer.effectAllowed = "move"
	}

	const handleClose = () => {
		setOpened(false)
		setSearchTerm("")
	}

	const handleOpen = () => {
		setOpened(true)
	}

	const blockTypeToDisabledConfig = () => {
		const config: Partial<Record<ChatBotFlowBlockType, DisabledBlockConfig>> = {
			"conditional-button": {
				disabled: true
			},
			"conditional-menu": {
				disabled: true
			}
		}

		return config
	}

	const isDisabledBlock = (blockType: ChatBotFlowBlockType): boolean => {
		const disabledBlocksMap = blockTypeToDisabledConfig()
		const blockConfig = disabledBlocksMap[blockType]

		return blockConfig?.disabled ?? false
	}

	const shouldShowBlockAlert = (blockType: ChatBotFlowBlockType): boolean => {
		const disabledBlocksMap = blockTypeToDisabledConfig()
		const blockConfig = disabledBlocksMap[blockType]
		return Boolean(blockConfig?.disabled)
	}

	return (
		<>
			{!opened && (
				<Button
					variant="contained"
					color="primary"
					onClick={handleOpen}
					className={classes.button}
				>
                    +
				</Button>
			)}

			<AsideDrawer
				opened={opened}
				onClose={handleClose}
				title="BLOCOS"
				anchor="left"
				childrenProps={{ contentClassName: classes.contentLayout }}
			>
				<Grid className={classes.searchSection}>
					<Input
						value={searchTerm}
						onChange={event => setSearchTerm(event.target.value)}
						placeholder="Nome, categoria ou descrição"
						startAdornment={
							<InputAdornment position="start">
								<SearchIcon />
							</InputAdornment>
						}
						endAdornment={
							searchTerm && (
								<InputAdornment position="end">
									<IconButton
										size="small"
										onClick={() => setSearchTerm("")}
									>
										<CloseIcon fontSize="small" />
									</IconButton>
								</InputAdornment>
							)
						}
						fullWidth
						className={classes.searchInput}
					/>
				</Grid>

				<Grid>
					<Tabs
						value={selectedTab}
						className={classes.chatTabs}
						onChange={(_, tab) => handleTabChange(tab as BlockCreatorTabsOptions)}
						classes={{
							indicator: classes.chatTabIndicator
						}}
						TabIndicatorProps={{ style: { transition: "none" } }}
					>
						<Tab className={classes.chatTab} label="Tudo" value="general" disableRipple />
						<Tab className={classes.chatTab} label="IA" value="ai" disableRipple />
					</Tabs>
				</Grid>

				<Grid className={classes.blocksSection}>
					<Grid container spacing={3} alignContent="flex-start">
						{filteredCategories.length === 0 && (
							<Grid item xs={12}>
								<Typography
									align="center"
									variant="h5"
									color="textSecondary"
								>
									{normalizedSearchTerm
										? `Nenhum bloco encontrado para "${normalizedSearchTerm}"`
										: "Nenhum bloco disponível nesta categoria."
									}
								</Typography>
							</Grid>
						)}

						{filteredCategories.map(category => (
							<Grid
								key={category.title}
								item
								xs={12}
							>
								<Grid
									container
									spacing={1}
								>
									<Grid
										item
										xs={12}
									>
										<ListItem
											disableGutters
											className={classes.categoryTitleContainer}
										>
											<ListItemIcon
												className={classes.categoryTitleIcon}
											>
												<CircleBulletIcon
													fontSize="small"
													style={{
														color: blockCategoryThemeConfig?.[category?.type]?.color?.blockIcon
													}}
												/>
											</ListItemIcon>

											<ListItemText
												primary={category.title}
												primaryTypographyProps={{
													variant: "h2",
													color: "textPrimary",
													className: classes.categoryTitleText
												}}
											/>
										</ListItem>
									</Grid>

									<Grid
										item
										xs={12}
									>
										<Grid
											container
											spacing={1}
										>
											{category.blocks.map(block => {
												const disabledBlock = isDisabledBlock(block.type)
												return !isBlockedBlock(block.type) && (
													<Grid
														item
														xs={12}
														key={block.type}
													>
														<Grid
															id={`chat-bot-block-list-${block.type}`}
															onDragStart={event => handleDragStart(event, block.type)}
															alignItems="flex-start"
															container
															draggable={!disabledBlock}
															className={classes.fullHeight}
															style={{
																userSelect: "none"
															}}
														>
															<Grid
																onDragStart={event => handleDragStart(event, block.type)}
																alignItems="flex-start"
																container
																direction="row"
																className={clsx({
																	[classes.blockContainer]: true,
																	[classes.fullHeight]: true
																})}
																style={{
																	borderLeftColor: disabledBlock ? colors.gray[300] : blockCategoryThemeConfig?.[category?.type]?.color?.blockIcon,
																	cursor: disabledBlock ? "default" : ""
																}}
																draggable={!disabledBlock}
															>
																{React.cloneElement(blockThemeConfig?.[block?.type]?.icon?.Component ||
                                                                    <DefaultIcon />, {
																	className: classes.blockIcon,
																	style: {
																		...(blockThemeConfig?.[block?.type]?.icon?.style || {}),
																		color: disabledBlock ? colors.gray[300] : blockCategoryThemeConfig?.[category?.type]?.color?.blockIcon
																	}
																})}

																<Divider orientation="vertical" size={2}/>

																<Grid
																	container
																	direction="column"
																	className={classes.blockInfoContainer}
																>
																	<Typography
																		align="left"
																		variant="h2"
																		color="textPrimary"
																		className={classes.blockInfoTitleText}
																		style={{
																			color: disabledBlock ? colors.gray[400] : ""
																		}}
																	>
																		{block.title}
																	</Typography>

																	<Divider orientation="horizontal" size={1}/>

																	<Typography
																		align="left"
																		variant="h5"
																		color="textPrimary"
																		className={classes.blockInfoDescriptionText}
																		style={{
																			color: disabledBlock ? colors.gray[400] : ""
																		}}
																	>
																		{block.description}
																	</Typography>
																</Grid>

																<Grid
																	item
																	className={clsx({
																		[classes.fullHeight]: true,
																		[classes.asideBlockContainer]: true
																	})}
																>
																	<Grid
																		container
																		direction="column"
																		justifyContent="space-between"
																		alignItems="flex-end"
																		className={classes.fullHeight}
																	>
																		{
																			shouldShowBlockAlert(block.type) &&
                                                                                <BlockAlertDialog block={block} />
																		}

																		{block.has_ai_capabilities && (
																			<Grid item>
																				<Grid
																					container
																					className={classes.fullHeight}
																				>
																					<Grid item>
																						<AIBlockTooltip>
																							<Grid>
																								<AIChip
																									size="small"
																								/>
																							</Grid>
																						</AIBlockTooltip>
																					</Grid>
																				</Grid>
																			</Grid>
																		)}
																	</Grid>
																</Grid>
															</Grid>
														</Grid>
													</Grid>
												)
											})}
										</Grid>
									</Grid>
								</Grid>
							</Grid>
						))}
					</Grid>
				</Grid>
			</AsideDrawer>
		</>
	)
}

export default BlockCreator
